import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, P, Textarea } from '@maui/elements'
import { GLOBAL_ORG_ID_PARAM, validators, store } from '@maui-mf/root/common-auth'
import { Spinner } from './Spinner'
import { navigateToUrl } from 'single-spa'
import { useAuthProvider } from '@maui-mf/root'
import { useSearchParam } from 'react-use'
import { isFramed } from '../utils'

const MSSO_CLIENT_ID_PARAM = 'mssocid'

const extractClientIdFromReturnTo = returnTo => {
  let clientId
  const url = new URL(returnTo, location.origin)

  if (url.searchParams.has(MSSO_CLIENT_ID_PARAM)) {
    clientId = url.searchParams.get(MSSO_CLIENT_ID_PARAM)
    url.searchParams.delete(MSSO_CLIENT_ID_PARAM)
  }

  return {
    returnTo: url.pathname + url.search + url.hash,
    clientId,
  }
}

// eslint-disable-next-line react/prop-types
const FramedLogin = ({ returnTo = '/' }) => {
  const onSubmit = e => {
    e.preventDefault()
    const data = e.target[0].value
    try {
      const { token, userinfo, profile } = JSON.parse(data)

      store.updateAndReload({
        returnTo,
        token,
        userinfo,
        profile,
      })
    } catch (e) {
      alert('Invalid JSON detected, please check your entry and retry.')
    }
  }

  return (
    <Box as="form" maxWidth="600px" onSubmit={onSubmit}>
      <P mt={0}>
        You are accessing the site from inside an iframe where login is unavailable.
      </P>
      <P>
        Please log into the same site you are previewing in another tab, use the developer tools
        to inspect local storage, paste the value found there under the key
        &ldquo;maui-mf-common-auth:store&rdquo; into the field below, then submit to continue.
      </P>
      <Textarea
        width="100%"
        rows="8"
        mb="14px"
      />
      <Button type="submit" variant="secondary">
        Submit
      </Button>
    </Box>
  )
}

export const Signin = () => {
  const { loggedIn, api, claims = {} } = useAuthProvider(false)
  const returnTo = useSearchParam('returnTo')
  const orgParam = useSearchParam(GLOBAL_ORG_ID_PARAM)
  const localClientId = useSearchParam(MSSO_CLIENT_ID_PARAM)

  // Get and validate the orgOverride param
  const orgOverride = useMemo(() => {
    // if orgParam is missing or invalid, return
    if (!orgParam || !validators.organizationId(orgParam)) {
      return
    }

    // if we're not logged in, return the param to trigger the SSO login
    if (!loggedIn) {
      return orgParam
    }

    // if the org is already current, return
    if (orgParam === claims.org) {
      console.warn('login org switch attempted for current org:', orgParam)
      return
    }

    // if the org isn't accessible to the user, return
    if (!claims.organizations.some(org => org.uuid === orgParam)) {
      console.warn('login org switch attempted for inaccessible org:', orgParam)
      return
    }

    // finally return the param to trigger the SSO login
    return orgParam
  }, [orgParam, loggedIn, claims])

  // A login will be required if the user is *not* logged in OR a valid org override
  // was determined
  const needsLogin = !loggedIn || !!orgOverride

  // We want to show the "framed" UI if login is necessary and the site is being
  // rendered in an iframe making login unavailable
  const showFramedUI = needsLogin && isFramed()

  // Create a state to track "mode" mostly. If we're showing the framed UI start out
  // in framed mode, otherwise begin in an "init" mode which we'll transition out of
  // into login.
  const [state, setState] = useState({ mode: showFramedUI ? 'framed' : 'init' })
  const { mode } = state

  const signin = useCallback(() => {
    setState({ mode: 'loading' })

    const options = {}

    if (returnTo) {
      try {
        const {
          clientId,
          returnTo: updatedReturnTo,
        } = extractClientIdFromReturnTo(returnTo)

        if (clientId) {
          options.clientId = clientId
        }

        options.state = { returnTo: updatedReturnTo }
      } catch (e) {
        console.error('error extracting clientId from returnTo', e.message)
      }
    }

    // NOTE there's some redundancy here in that this route expects the client ID
    // to be in the returnTo path as the anticipated normal behavior, but it also
    // allows for passing the client ID directly to this route, which will take
    // precedence if it occurs. I.e. hitting the site unauthenticated at any path
    // should result in a route like (url encoded):
    //
    //     /auth/signin?returnTo=/?clientId=1234
    //
    // However if the signing page is visited directly, it will also read it from
    // the current URL, like:
    //
    //     /auth/signin?clientId=1234
    //
    if (localClientId) {
      options.clientId = localClientId
    }

    if (orgOverride) {
      options.orgOverride = orgOverride
    }

    api.initiateLogin(options).catch(e => {
      console.error(e)
      setState({ mode: 'error', error: e })
    })
  }, [api, localClientId, orgOverride, returnTo])

  useEffect(() => {
    // if no login is necessary immediately navigate to returnTo
    if (!needsLogin) {
      navigateToUrl(returnTo || '/')
      return
    }

    // We only auto login from the initial state. Any other mode comes with a UI
    // and may or may not present a button to sign in manually.
    if (mode === 'init') {
      signin()
    }
  }, [needsLogin, returnTo, mode, signin])

  // While in init mode, render nothing, avoiding a flash of content before we jump
  // into SSO loading or navigating back to the returnTo
  if (mode === 'init') {
    return null
  }

  return (
    <Box
      flex="1"
      display="flex"
      flexDirection="column"
      width="100%"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
    >
      {mode === 'framed' &&
        <FramedLogin returnTo={returnTo} />
      }

      {mode === 'error' && <>
        <P mt={0} mb="spaceGapXxLarge" color="colorActionDestructive">
          An unexpected error was encountered attempting to log in
        </P>
        <Button
          variant="secondary"
          onClick={signin}
        >
          Try again
        </Button>
      </>}

      {mode === 'loading' && <>
        <Spinner />
        <P>Redirecting to Single Sign On</P>
      </>}
    </Box>
  )
}

export default Signin
